import type { ReactNode } from 'react'

interface PolicyShellProps {
  title: string
  /** ISO date string for the "last updated" footer (e.g. '2026-05-25'). */
  updated: string
  children: ReactNode
}

/**
 * Shared chrome for the four policy pages (Privacy / Security /
 * Accessibility / Terms of Use). Centers the content in a comfortable
 * reading column and gives every policy the same visual rhythm.
 */
export function PolicyShell({ title, updated, children }: PolicyShellProps) {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
      <header className="mb-8">
        <span className="inline-block text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--color-accent)] font-[var(--font-mono)]">
          Policies
        </span>
        <h1 className="mt-2 text-[34px] sm:text-[40px] font-semibold tracking-tight text-[var(--color-text-strong)] leading-[1.1]">
          {title}
        </h1>
        <p className="mt-2 text-[13px] text-[var(--color-muted)] font-[var(--font-mono)]">
          Last updated · {updated}
        </p>
      </header>
      <div className="flex flex-col gap-8">{children}</div>
    </article>
  )
}

interface PolicySectionProps {
  heading: string
  children: ReactNode
}

/**
 * One section of a policy page. Styles the H2 + body prose with consistent
 * spacing and tints muted secondary content.
 */
export function PolicySection({ heading, children }: PolicySectionProps) {
  return (
    <section>
      <h2 className="text-[18px] font-semibold tracking-tight text-[var(--color-text-strong)]">
        {heading}
      </h2>
      <div className="mt-3 flex flex-col gap-3 text-[14.5px] leading-relaxed text-[var(--color-text)] [&_a]:text-[var(--color-accent)] [&_a]:underline [&_a]:underline-offset-2 [&_code]:font-[var(--font-mono)] [&_code]:text-[13px] [&_code]:text-[var(--color-text-strong)] [&_strong]:font-semibold [&_strong]:text-[var(--color-text-strong)] [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-1.5">
        {children}
      </div>
    </section>
  )
}
